import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Key, AlertCircle, Clock, Mail, Plus, ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { fmtUSD, FLAGS } from "@/lib/utils";
import { SecureImage } from "@/components/ImageUploader";

export default function DeveloperDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [interests, setInterests] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, i] = await Promise.all([api.get("/developer/dashboard"), api.get("/interests/mine")]);
      setStats(s.data);
      setInterests(i.data);
    })();
  }, []);

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Developer Portal</p>
          <h1 className="text-4xl font-bold text-neutral-900 mt-2">
            Hello, <span className="serif italic text-propera-gold">{firstName}</span>
          </h1>
          <p className="text-neutral-500 mt-2">Manage your investors, payments and developments.</p>
        </div>
        <Link to="/properties" data-testid="add-development-cta" className="inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold transition glow-primary">
          <Plus size={16} /> Add Development
        </Link>
      </div>

      {/* KPI Bento */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
        <KPI to="/properties" testId="kpi-developments" icon={Building2} color="bg-propera-light text-propera" label="Developments" value={stats?.developments ?? 0} />
        <KPI to="/leads" testId="kpi-investors" icon={Users} color="bg-blue-50 text-blue-600" label="Investors" value={stats?.investors ?? 0} />
        <KPI to="/properties" testId="kpi-allocated" icon={Key} color="bg-purple-50 text-purple-600" label="Allocated Units" value={stats?.allocated_units ?? 0} />
        <KPI to="/leads?filter=not_proceeding" testId="kpi-overdue" icon={AlertCircle} color="bg-red-50 text-red-600" label="Overdue" value={stats?.overdue ?? 0} />
        <KPI to="/leads?filter=meeting_scheduled" testId="kpi-due" icon={Clock} color="bg-amber-50 text-amber-600" label="Due This Month" value={stats?.due_this_month ?? 0} />
        <KPI to="/leads?filter=new_interest" testId="kpi-new-interests" icon={Mail} color="bg-green-50 text-emerald-600" label="New Interests" value={stats?.new_interests ?? 0} />
      </div>

      {/* Secondary shortcuts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <ShortcutCard to="/messages" testId="short-messages" label="Messages" hint="Chat threads" />
        <ShortcutCard to="/leads" testId="short-leads" label="Leads" hint="Pipeline board" />
        <ShortcutCard to="/notifications" testId="short-notifications" label="Notifications" hint="Recent alerts" />
        <ShortcutCard to="/help" testId="short-help" label="Help & Support" hint="Get help" />
      </div>

      {/* Developments + Recent Interests */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-4">
            <h2 className="text-xl font-bold text-neutral-900">Your Developments</h2>
            <Link to="/properties" className="text-sm font-semibold text-propera hover:underline">Manage →</Link>
          </div>
          {(!stats?.developments_list || stats.developments_list.length === 0) ? (
            <EmptyState
              icon={Building2}
              title="No developments yet"
              body="Add your first development to start attracting diaspora investors."
              cta={<Link to="/properties" data-testid="empty-add-dev" className="inline-flex items-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-5 py-2.5 font-semibold text-sm"><Plus size={14} /> Add Development</Link>}
            />
          ) : (
            <div className="space-y-3">
              {stats.developments_list.map((d) => (
                <Link key={d.id} to={`/development/${d.id}`} className="flex items-center gap-4 soft-card p-4 soft-card-hover">
                  <SecureImage src={d.cover_image} alt="" className="w-20 h-20 rounded-2xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-neutral-900 truncate">{d.name}</p>
                    <p className="text-xs text-neutral-500 flex items-center gap-1 mt-1"><MapPin size={12} /> {d.city}, {d.country}</p>
                    <p className="text-sm font-semibold text-propera tabular mt-2">From {fmtUSD(d.starting_price)}</p>
                  </div>
                  <ArrowRight size={16} className="text-neutral-400" />
                </Link>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="flex items-end justify-between mb-4">
            <h2 className="text-xl font-bold text-neutral-900">Recent Leads</h2>
            <Link to="/leads" className="text-sm font-semibold text-propera hover:underline">All →</Link>
          </div>
          {interests.length === 0 ? (
            <div className="soft-card p-8 text-center">
              <p className="text-sm text-neutral-500">No leads yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {interests.slice(0, 5).map((i) => (
                <Link key={i.id} to={i.conversation_id ? `/messages?c=${i.conversation_id}` : `/leads`} data-testid={`recent-lead-${i.id}`} className="block soft-card p-4 soft-card-hover">
                  <p className="font-semibold text-neutral-900 text-sm">{i.investor?.name}</p>
                  <p className="text-xs text-neutral-500">{i.development?.name}</p>
                  {i.unit && <p className="text-xs text-propera mt-1">{i.unit.label} · {i.unit.bedrooms}</p>}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, color, label, value, to, testId }) {
  const inner = (
    <>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon size={18} />
      </div>
      <p className="text-3xl font-bold tabular text-neutral-900">{value}</p>
      <p className="text-xs text-neutral-500 mt-1">{label}</p>
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} className="soft-card p-5 soft-card-hover block">
        {inner}
      </Link>
    );
  }
  return <div className="soft-card p-5">{inner}</div>;
}

function ShortcutCard({ to, label, hint, testId }) {
  return (
    <Link to={to} data-testid={testId} className="soft-card p-4 soft-card-hover flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-neutral-900">{label}</p>
        <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>
      </div>
      <ArrowRight size={16} className="text-neutral-400" />
    </Link>
  );
}

function EmptyState({ icon: Icon, title, body, cta }) {
  return (
    <div className="rounded-3xl border border-dashed border-neutral-200 bg-white p-10 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4">
        <Icon size={22} />
      </div>
      <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
      <p className="text-sm text-neutral-500 max-w-md mx-auto mt-1">{body}</p>
      <div className="mt-5">{cta}</div>
    </div>
  );
}
